import { Select } from 'antd';
import { useState } from 'react';
import { APPLICATION_TYPES } from '../../cyber/constants';
import { useQuery } from '@apollo/client';
import TierBadge from '../../dataInputs/tiers/components/TierBadge';

import { GET_TIER_OPTIONS } from '../graphql';

type TierOption = {
  value: string;
  label: string;
  color: string;
};

type ApplicationFiltersProps = {
  locationName?: string;
  onFiltersChange: (filters: {
    applicationType: string | null;
    selectedTierUuid: string | null;
    drpInPlace: boolean | null;
  }) => void;
};

export const ApplicationFilters = ({
  locationName,
  onFiltersChange,
}: ApplicationFiltersProps) => {
  const [applicationType, setApplicationType] = useState<string | null>(null);
  const [selectedTierUuid, setSelectedTierUuid] = useState<string | null>(null);
  const [drpInPlace, setDrpInPlace] = useState<boolean | null>(null);

  const { data: tierData } = useQuery(GET_TIER_OPTIONS);

  const applicationTypeOptions = [
    { value: APPLICATION_TYPES.ENTERPRISE, label: 'Enterprise' },
    { value: APPLICATION_TYPES.LOCATIONS, label: 'Location' },
  ];

  const tierOptions: TierOption[] =
    tierData?.getTiers?.map(
      (tier: { uuid: string; name: string; color: string }) => ({
        value: tier.uuid,
        label: tier.name,
        color: tier.color,
      }),
    ) || [];

  const drpOptions = [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' },
  ];

  const clearFilters = () => {
    setApplicationType(null);
    setSelectedTierUuid(null);
    setDrpInPlace(null);
    onFiltersChange({
      applicationType: null,
      selectedTierUuid: null,
      drpInPlace: null,
    });
  };

  const handleApplicationTypeChange = (value: string | null) => {
    setApplicationType(value);
    onFiltersChange({ applicationType: value, selectedTierUuid, drpInPlace });
  };

  const handleTierChange = (value: string | null) => {
    setSelectedTierUuid(value);
    onFiltersChange({ applicationType, selectedTierUuid: value, drpInPlace });
  };

  const handleDrpChange = (value: boolean | null) => {
    setDrpInPlace(value);
    onFiltersChange({ applicationType, selectedTierUuid, drpInPlace: value });
  };

  const hasActiveFilters =
    applicationType !== null ||
    selectedTierUuid !== null ||
    drpInPlace !== null;

  return (
    <div className="flex gap-4 py-4 items-center">
      {locationName && (
        <Select
          value={locationName || 'Loading...'}
          disabled
          placeholder="Location"
          size="large"
          className="bg-gray-100"
        />
      )}
      {locationName && (
        <Select
          placeholder="Application Type"
          value={applicationType}
          onChange={handleApplicationTypeChange}
          allowClear
          size="large"
          style={{ width: 150 }}
          options={applicationTypeOptions}
        />
      )}
      <Select
        placeholder="Tier"
        value={selectedTierUuid}
        onChange={handleTierChange}
        allowClear
        size="large"
        style={{ width: 90 }}
        options={tierOptions}
        dropdownRender={() => (
          <div>
            {tierOptions.map((option: TierOption) => (
              <div
                key={option.value}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                onClick={() => handleTierChange(option.value)}
              >
                <TierBadge
                  name={option.label}
                  color={option.color}
                  style={{ margin: 0 }}
                />
              </div>
            ))}
          </div>
        )}
      />
      <Select
        placeholder="DRP"
        value={drpInPlace}
        onChange={handleDrpChange}
        allowClear
        size="large"
        style={{ width: 100 }}
        options={drpOptions}
      />
      <button
        disabled={!hasActiveFilters}
        onClick={clearFilters}
        className="px-3 py-2 text-base text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
        style={{ height: '40px' }}
      >
        Clear Filters
      </button>
    </div>
  );
};
